/**
 * Minimal, dependency-free i18n.
 *
 * A React context holds the active locale and a translate function. The choice
 * persists to localStorage under `pmw.locale` and drives <html lang>. Components
 * call useT() for the translate function, or useLocale() for the full control
 * (current locale + setter). No runtime library — the whole thing is a context,
 * two dictionaries, and a lookup with {name} interpolation.
 */

/* eslint-disable react-refresh/only-export-components --
   Canonical React context module: the provider is intentionally colocated with
   its hooks (useT/useLocale) and constants (LOCALES). react-refresh's HMR rule
   is irrelevant for this foundational module, which changes rarely. */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { isRoute, ROUTE_WELCOME, ROUTE_OAUTH_CALLBACK } from '../lib/routes';

import { en, type MessageKey } from './en';
import { ko } from './ko';
import { zh } from './zh';
import { ja } from './ja';
import { vi } from './vi';
import { th } from './th';

export type Locale = 'en' | 'zh' | 'ja' | 'ko' | 'vi' | 'th';

/** Locales offered in the UI, with their own-language display names (ordered to
 *  match the sibling p2p platform's language picker). */
export const LOCALES: ReadonlyArray<{ value: Locale; label: string }> = [
  { value: 'en', label: 'English' },
  { value: 'zh', label: '中文' },
  { value: 'ja', label: '日本語' },
  { value: 'ko', label: '한국어' },
  { value: 'vi', label: 'Tiếng Việt' },
  { value: 'th', label: 'ไทย' },
];

const DICTS: Record<Locale, Record<MessageKey, string>> = { en, zh, ja, ko, vi, th };
const LOCALE_VALUES: ReadonlySet<string> = new Set(LOCALES.map((l) => l.value));
const isLocale = (v: unknown): v is Locale => typeof v === 'string' && LOCALE_VALUES.has(v);
const STORAGE_KEY = 'pmw.locale';
const DEFAULT_LOCALE: Locale = 'en';

// Per-language terms acceptance. Localised UI beyond English is a region-gated feature, so
// the legal notice must be accepted before a non-English language is used — both on the
// welcome screen and when switching on the language page. Acceptance is stored per locale and
// versioned, so bumping TERMS_VERSION re-prompts everyone. English never needs acceptance.
const TERMS_KEY = 'pmw.terms';
const TERMS_VERSION = 1;

function acceptedTermsSet(): Set<Locale> {
  try {
    const parsed = JSON.parse(localStorage.getItem(TERMS_KEY) ?? 'null') as {
      v?: number;
      locales?: unknown;
    } | null;
    if (!parsed || parsed.v !== TERMS_VERSION || !Array.isArray(parsed.locales)) return new Set();
    return new Set(parsed.locales.filter(isLocale));
  } catch {
    return new Set();
  }
}

/** Whether the language terms are satisfied for a locale (always true for English). */
export function hasAcceptedTerms(locale: Locale): boolean {
  return locale === 'en' || acceptedTermsSet().has(locale);
}

/** Record acceptance of the language terms for a (non-English) locale. */
export function acceptTerms(locale: Locale): void {
  if (locale === 'en') return;
  try {
    const set = acceptedTermsSet();
    set.add(locale);
    localStorage.setItem(TERMS_KEY, JSON.stringify({ v: TERMS_VERSION, locales: [...set] }));
  } catch {
    // Best effort; if it can't persist, the notice simply shows again next time.
  }
}

/**
 * The initial locale AND whether it is a *settled* choice — the welcome gate uses
 * `chosen` to decide who is a genuine first-time visitor.
 *
 * `chosen` is true (do NOT show /welcome) when any of these hold: an explicit
 * stored choice (Settings switcher or the welcome screen); OR any other `pmw.*`
 * key exists, i.e. a returning user with a session / device / prior state; OR
 * localStorage is unavailable (never trap a user whose choice can't persist).
 * Only a genuinely fresh install — no stored choice and no prior app state — is
 * unsettled, falling back to the browser's preferred language for /welcome.
 */
export function initialLocaleState(): { locale: Locale; chosen: boolean } {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored)) return { locale: stored, chosen: true };
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key !== STORAGE_KEY && key.startsWith('pmw.')) {
        // A returning user (session / device / OAuth state) — not a first visit.
        return { locale: detectBrowserLocale(), chosen: true };
      }
    }
  } catch {
    return { locale: detectBrowserLocale(), chosen: true };
  }
  return { locale: detectBrowserLocale(), chosen: false };
}

/** The first supported locale among the browser's preferences (matched on the
 *  primary subtag, so zh-CN / zh-TW → zh), else the default. */
function detectBrowserLocale(): Locale {
  try {
    const prefs = navigator.languages?.length ? navigator.languages : [navigator.language];
    for (const raw of prefs) {
      const code = (raw || '').toLowerCase().split('-')[0];
      if (isLocale(code)) return code;
    }
  } catch {
    // navigator unavailable — fall through.
  }
  return DEFAULT_LOCALE;
}

/** The language the welcome screen always renders in. Deliberately separate from
 *  DEFAULT_LOCALE: that is the browser-detection fallback, while this is a product
 *  invariant ("the first screen is always English") and the two may diverge. */
export const WELCOME_LOCALE: Locale = 'en';

/**
 * The language the DOCUMENT is currently showing — i.e. what `<html lang>` must say.
 *
 * This is NOT simply the active locale: the welcome screen always renders in English
 * (WELCOME_LOCALE) regardless of the stored/detected locale, so the two diverge there.
 * `chosen` alone is the wrong proxy — it is visitor history, not the rendered route —
 * so the routes below mirror WelcomeGate (App.tsx) exactly.
 *
 * Getting this right matters beyond assistive tech: `--font-sans` is selected by
 * `html[lang='ja']` (tokens.css), so a stale value downloads a multi-megabyte CJK
 * webfont for a screen that does not render that script.
 */
export function resolveDocumentLocale({
  locale,
  localeChosen,
  pathname,
  authed,
}: {
  locale: Locale;
  localeChosen: boolean;
  pathname: string;
  authed: boolean;
}): Locale {
  // The welcome screen itself. An authenticated visitor is redirected away from it
  // and never sees it, so only the unauthenticated case renders English here.
  if (isRoute(pathname, ROUTE_WELCOME) && !authed) return WELCOME_LOCALE;
  // Any route WelcomeGate will redirect to the (English) welcome screen. Mirrors its
  // condition: authed users and the OAuth callback are exempt from the gate.
  if (!localeChosen && !authed && !isRoute(pathname, ROUTE_OAUTH_CALLBACK)) return WELCOME_LOCALE;
  return locale;
}

/** Substitute {name} placeholders in a template from vars. */
function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name) =>
    name in vars ? String(vars[name]) : match,
  );
}

/** Translate a key in a SPECIFIC locale, without touching the active one — used to preview
 *  a language the visitor is choosing but has not yet committed to (welcome / Settings). */
export function translateIn(
  locale: Locale,
  key: MessageKey,
  vars?: Record<string, string | number>,
): string {
  return interpolate(DICTS[locale][key] ?? en[key] ?? key, vars);
}

/**
 * Translate in the page's ACTIVE locale from a module that has no React context.
 *
 * Services throw user-facing errors and cannot call the hook, so without this the
 * whole service layer surfaces English to every locale — which it largely still
 * does (see `messageFor` in services/devices.ts and the literals in lib/webauthn).
 * This is the seam to move those onto.
 *
 * The active locale is read from `html[lang]`, which the provider stamps in
 * main.tsx and App.tsx — the one source reachable from a plain function. An
 * unrecognised or absent value falls back to English rather than throwing, since a
 * missing translation must never be the reason an error cannot be shown.
 */
export function translate(key: MessageKey, vars?: Record<string, string | number>): string {
  const lang = typeof document === 'undefined' ? '' : document.documentElement.lang;
  return translateIn(isLocale(lang) ? lang : 'en', key, vars);
}

export type { MessageKey };
export type TranslateFn = (key: MessageKey, vars?: Record<string, string | number>) => string;

interface I18nContextValue {
  locale: Locale;
  setLocale: (next: Locale) => void;
  t: TranslateFn;
  /**
   * Whether a locale is settled — a stored choice, a returning user, or a pick
   * made this session. False only for a genuine first-time visitor. The welcome
   * gate reads this (in-memory) instead of localStorage, so a failed persist can
   * never loop it back to /welcome.
   */
  localeChosen: boolean;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState(initialLocaleState);
  const { locale, chosen: localeChosen } = state;

  const setLocale = useCallback((next: Locale) => {
    // Mark chosen in memory too, so a failed persist (quota) below cannot re-trap
    // the welcome gate — the choice still holds for this session.
    setState({ locale: next, chosen: true });
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Best effort; the choice still applies for this session.
    }
  }, []);

  // `<html lang>` is NOT owned here: it depends on the current route (the welcome
  // screen always renders English), and this provider sits outside the router. It is
  // applied by useDocumentLanguage() in App via resolveDocumentLocale(), with a
  // matching pre-render pass in main.tsx. Keeping a single writer avoids the two
  // fighting — a route-blind effect here would overwrite the correct value on mount.

  const t = useCallback<TranslateFn>(
    (key, vars) => {
      // Fall back to English, then the raw key, so a gap never crashes the UI.
      const template = DICTS[locale][key] ?? en[key] ?? key;
      return interpolate(template, vars);
    },
    [locale],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ locale, setLocale, t, localeChosen }),
    [locale, setLocale, t, localeChosen],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** Full i18n control: current locale, the setter, and the translate function. */
export function useLocale(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useLocale must be used within <LanguageProvider>');
  return ctx;
}

/** The translate function alone — the common case in views. */
export function useT(): TranslateFn {
  return useLocale().t;
}
